<template>
  <video
    ref="videoElement"
    v-bind="attrs"
    :class="[bem.b()]"
    :src="src"
    :poster="poster"
    :autoplay="autoplay"
    :loop="loop"
    :preload="preload"
    :muted="muted"
    :controls="controls"
    :width="width"
    :height="height"
  >
    <source :src="src" :type="getType(format)" />
    您的浏览器不支持视频播放。
  </video>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { onMounted, ref, useAttrs, watch } from 'vue'
import { videoPlayerProps } from './video-player'

defineOptions({ name: 'LlVideoPlayer' })

const attrs = useAttrs()
const bem = createNamespace('video-player')

const props = defineProps(videoPlayerProps)

// 响应式数据
const videoElement = ref<HTMLVideoElement>()

const videoTypeMap = {
  MP4: 'video/mp4',
  WebM: 'video/webm',
  OGG: 'video/ogg',
  MOV: 'video/quicktime',
  AVI: 'video/x-msvideo',
  WMV: 'video/x-ms-wmv',
  FLV: 'video/x-flv',
  MKV: 'video/x-matroska',
}

const getType = (format: string) => {
  return videoTypeMap[format.toUpperCase()] || 'video/mp4'
}

/**
 * 浏览器自动播放策略：页面刷新后 autoplay 属性可能被阻止
 * 通过 JS 调用 play() 并在被拦截时静音后重试，确保自动播放生效
 */
const tryAutoplay = () => {
  const video = videoElement.value
  if (!video || !props.autoplay) return
  video.play().catch(() => {
    // 浏览器阻止了有声自动播放，静音后再试
    video.muted = true
    video.play().catch(() => {
      // 静音播放也失败，忽略
    })
  })
}

onMounted(() => {
  tryAutoplay()
})

watch(
  () => props.src,
  () => {
    // src 变化后重新触发自动播放
    const video = videoElement.value
    if (!video) return
    video.load()
    tryAutoplay()
  },
)
</script>
